import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../Auth';
import { useAxios } from '../../../AxiosHandler';
import Modal from '../../Shared/Modal/Modal';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

import { Col, Container, Row } from 'react-bootstrap';

import styles from './SelectedRequestBreeding.module.scss';
import Card from '../../Shared/Card/Card';
import { general } from '../../../general';
import PedigreeCard from '../Statistics/Cards/PedigreeCard/PedigreeCard';
import { usePedigree } from '../Statistics/Cards/PedigreeCard/usePedigree';
import CollapsibleSection from '../../Shared/CollapsibleSection/CollapsibleSection';
import CombinationAssessment from './Parts/CombinationAssessment';
import DiseaseAlert from './Parts/DiseaseAlert';
import DogSummaryCard from './Parts/DogSummaryCard';
import Skeleton from '../../Shared/Skeleton/Skeleton';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

function SelectedRequestBreeding(props: SelectedRequestBreedingProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();
    const location = useLocation();

    // Listesidas query-streng (fanen, eller åpen seksjon på /admin) følger med hit
    // i history state, slik at knappene under leverer brukeren tilbake til det de
    // kom fra og ikke til standardvisningen. Åpnet man detaljsiden via en direkte
    // lenke finnes ingen state, og vi lander på listesida uendret.
    const listUrl = "/partnersoknader" + ((location.state as any)?.listSearch ?? "");

    const [selectedReg, setSelectedReg] = useState<any | undefined>();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [maleDogSearchTerm, setMaleDogSearchTerm] = useState<string>("");
    const [maleDogSearchResults, setMaleDogSearchResults] = useState<any[]>([]);
    const [isMaleDogSearching, setIsMaleDogSearching] = useState<boolean>(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [publishToList, setPublishToList] = useState(false);
    const [sendEditLink, setSendEditLink] = useState(false);
    const [editLinkEmail, setEditLinkEmail] = useState('');
    const [isAccepting, setIsAccepting] = useState(false);
    const [simulationResult, setSimulationResult] = useState<any | null>(null);
    const [isSimulating, setIsSimulating] = useState<boolean>(false);
    const [familyTreeGenerations, setFamilyTreeGenerations] = useState<number>(2);
    // Diseases and half siblings switched off in the stamtavle; the disease
    // warning follows them
    const [hiddenDiseases, setHiddenDiseases] = useState<Set<string>>(new Set());
    const [showHalfSiblings, setShowHalfSiblings] = useState<boolean>(false);

    // The matched dogs' ids come with the request (DOGMATCH / MALEDOGMATCH), so
    // everything that needs them can start as soon as the request is loaded
    const femaleId: number | undefined = selectedReg?.DOGMATCH?.ANIMAL_ID ?? undefined;
    const maleId: number | undefined = selectedReg?.MALEDOGMATCH?.ANIMAL_ID ?? undefined;

    const { data: femaleDogUseLeash } = useQuery({
        queryKey: ['dog-use-leash', femaleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + femaleId + "/use/leash/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!femaleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: femaleDogUseLoose } = useQuery({
        queryKey: ['dog-use-loose', femaleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + femaleId + "/use/loose/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!femaleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: femaleDogUseHare } = useQuery({
        queryKey: ['dog-use-hare', femaleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + femaleId + "/use/hare/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!femaleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // No bird tests on prod (2.7.2_HOTFIX NO BIRD ON PROD)
    // const { data: femaleDogUseBird } = useQuery({
    //     queryKey: ['dog-use-bird', femaleId],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + femaleId + "/use/bird/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!femaleId,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    // const { data: femaleDogUseBirdfetch } = useQuery({
    //     queryKey: ['dog-use-birdfetch', femaleId],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + femaleId + "/use/birdfetch/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!femaleId,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    const { data: maleDogUseLeash } = useQuery({
        queryKey: ['dog-use-leash', maleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + maleId + "/use/leash/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!maleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: maleDogUseLoose } = useQuery({
        queryKey: ['dog-use-loose', maleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + maleId + "/use/loose/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!maleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: maleDogUseHare } = useQuery({
        queryKey: ['dog-use-hare', maleId],
        queryFn: () => axios.single({
            method: 'get',
            url: "/api/statistics/dog/" + maleId + "/use/hare/count",
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
        enabled: !!maleId,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    // const { data: maleDogUseBird } = useQuery({
    //     queryKey: ['dog-use-bird', maleId],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + maleId + "/use/bird/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!maleId,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    // const { data: maleDogUseBirdfetch } = useQuery({
    //     queryKey: ['dog-use-birdfetch', maleId],
    //     queryFn: () => axios.single({
    //         method: 'get',
    //         url: "/api/statistics/dog/" + maleId + "/use/birdfetch/count",
    //         responseType: 'json',
    //         headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
    //     }).then((res: any) => res.data?.[0]?.TESTS_COUNT ?? 0),
    //     enabled: !!maleId,
    //     staleTime: 5 * 60 * 1000,
    //     gcTime: 10 * 60 * 1000,
    // });

    function copyToClipboard(value: string, key: string) {
        navigator.clipboard.writeText(value ?? "");
        setCopiedKey(key);
        setTimeout(() => setCopiedKey(null), 1500);
    }

    function getBreedingDog() {
        axios.single({
            method: 'get',
            url: "/api/schema/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    async function acceptWithOptions() {
        setIsAccepting(true);
        try {
            const res: any = await axios.single({
                method: 'post',
                url: "/api/schema/accept/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
                responseType: 'json',
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
            });

            const breedingId = res.data?.breedingId;
            const dogId = res.data?.dogId;

            if (publishToList && breedingId && dogId) {
                await axios.single({
                    method: 'post',
                    url: "/api/breedings/" + breedingId + "/breedingapprovedpublish",
                    responseType: 'json',
                    headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
                    data: { dogid: dogId, publish_public: true }
                }).catch((err: any) => console.log(err));
            }

            if (sendEditLink && breedingId && editLinkEmail) {
                const linkRes: any = await axios.single({
                    method: 'post',
                    url: "/api/dogs/puppylisting/accesslink/" + auth.selectedBreed,
                    responseType: 'json',
                    headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
                    data: { breedingsId: breedingId }
                }).catch((err: any) => { console.log(err); return null; });

                const accessKey = linkRes?.data?.accessKey;
                if (accessKey) {
                    await axios.single({
                        method: 'post',
                        url: "/api/dogs/puppylisting/accesslink/sendemail/" + accessKey + "/" + auth.selectedBreed,
                        responseType: 'json',
                        headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
                        data: { email: editLinkEmail, damName: selectedReg?.DOGMATCH?.NAME || null }
                    }).catch((err: any) => console.log(err));
                }
            }

            navigate(listUrl);
        } catch (err: any) {
            console.log(err);
            setIsAccepting(false);
        }
    }

    function rejectBreeding() {
        axios.single({
            method: 'post',
            url: "/api/schema" + "/reject/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function deleteBreeding() {
        axios.single({
            method: 'delete',
            url: "/api/schema" + "/delete/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res) => {
            navigate(listUrl);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function searchDog() {
        if (!searchTerm || searchTerm.trim() === "") {
            return;
        }
        
        setIsSearching(true);
        axios.single({
            method: 'post',
            url: "/api/dogs/searchdogs",
            data: {
                breedid: auth.selectedBreed,
                search: searchTerm,
                sex: "female",
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSearchResults(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsSearching(false);
        });
    }

    function selectDog(dog: any) {
        axios.single({
            method: 'post',
            url: "/api/schema/updateDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            data: {
                animalId: dog.ANIMAL_ID,
                regnumber: dog.REGNO
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
            setSearchResults([]);
            setSearchTerm("");
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function removeDogMatch() {
        axios.single({
            method: 'post',
            url: "/api/schema/removeDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function searchMaleDog() {
        if (!maleDogSearchTerm || maleDogSearchTerm.trim() === "") {
            return;
        }

        setIsMaleDogSearching(true);
        axios.single({
            method: 'post',
            url: "/api/dogs/searchdogs",
            data: {
                breedid: auth.selectedBreed,
                search: maleDogSearchTerm,
                sex: "male",
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setMaleDogSearchResults(res.data);
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsMaleDogSearching(false);
        });
    }

    function selectMaleDog(dog: any) {
        axios.single({
            method: 'post',
            url: "/api/schema/updateMaleDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            data: {
                animalId: dog.ANIMAL_ID,
                regnumber: dog.REGNO
            },
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
            setMaleDogSearchResults([]);
            setMaleDogSearchTerm("");
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function removeMaleDogMatch() {
        axios.single({
            method: 'post',
            url: "/api/schema/removeMaleDogMatch/" + params.schemakey + "/" + params.breedregid + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSelectedReg(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function runBreedingSimulation(maleAnimalId: any, femaleAnimalId: any) {
        setIsSimulating(true);
        setSimulationResult(null);
        axios.single({
            method: 'post',
            url: "/api/dogs/simulatebreeding",
            responseType: 'json',
            data: {
                dogId: maleAnimalId,
                partnerIds: [femaleAnimalId]
            },
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            if (res.data?.length > 0) {
                setSimulationResult(res.data[0]);
            }
        }).catch((err: any) => {
            console.log(err);
        }).finally(() => {
            setIsSimulating(false);
        });
    }

    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    useEffect(() => {
        if (params.breedregid != undefined && auth.selectedBreed != undefined && auth.selectedBreed != "") {
            getBreedingDog();
        }
    }, [params.breedregid, auth.selectedBreed]);

    useEffect(() => {
        if (selectedReg?.REGNUMBER != undefined && selectedReg?.MALE_REGNUMBER != undefined) {
            runBreedingSimulation(selectedReg.MALEDOGMATCH?.ANIMAL_ID, selectedReg.DOGMATCH?.ANIMAL_ID);
        } else {
            setSimulationResult(null);
        }
    }, [selectedReg?.REGNUMBER, selectedReg?.MALE_REGNUMBER]);

    // All test starts, once every count has arrived
    // Bird and birdfetch left out on prod (2.7.2_HOTFIX NO BIRD ON PROD)
    const femaleTestStarts = [femaleDogUseLeash, femaleDogUseLoose, femaleDogUseHare /*, femaleDogUseBird, femaleDogUseBirdfetch*/].every(n => n != null)
        ? femaleDogUseLeash + femaleDogUseLoose + femaleDogUseHare /*+ femaleDogUseBird + femaleDogUseBirdfetch*/
        : undefined;
    const maleTestStarts = [maleDogUseLeash, maleDogUseLoose, maleDogUseHare /*, maleDogUseBird, maleDogUseBirdfetch*/].every(n => n != null)
        ? maleDogUseLeash + maleDogUseLoose + maleDogUseHare /*+ maleDogUseBird + maleDogUseBirdfetch*/
        : undefined;

    const femalePedigree = usePedigree(femaleId, familyTreeGenerations);
    const malePedigree = usePedigree(maleId, familyTreeGenerations);

    return (
        <div className="maincontent">
            <Modal
                title={t("selectedrequestbreeding.acceptmodal.title")}
                show={showAcceptModal}
                onClose={() => { if (!isAccepting) setShowAcceptModal(false); }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={publishToList}
                            onChange={(e) => setPublishToList(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>{t("selectedrequestbreeding.acceptmodal.publishtopuppylist")}</span>
                    </label>
                    <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={sendEditLink}
                                onChange={(e) => setSendEditLink(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span>{t("selectedrequestbreeding.acceptmodal.sendeditlink")}</span>
                        </label>
                        {sendEditLink && (
                            <input
                                type="email"
                                className="form-control"
                                placeholder={t("selectedrequestbreeding.acceptmodal.email")}
                                value={editLinkEmail}
                                onChange={(e) => setEditLinkEmail(e.target.value)}
                                style={{ marginTop: '10px' }}
                            />
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setShowAcceptModal(false)}
                            disabled={isAccepting}
                        >
                            {t("selectedrequestbreeding.acceptmodal.cancel")}
                        </button>
                        <button
                            className="btn btn-primary btn-positive"
                            onClick={acceptWithOptions}
                            disabled={isAccepting || (sendEditLink && !editLinkEmail)}
                        >
                            {isAccepting ? t("selectedrequestbreeding.acceptmodal.accepting") : t("selectedrequestbreeding.acceptmodal.confirm")}
                        </button>
                    </div>
                </div>
            </Modal>
            <div className={styles.selectedrequestbreeding}>
                <h1>{t("selectedrequestbreeding.title")}</h1>
                <hr/>
                
                <div className={styles.actions}>
                    <button className="btn btn-primary" onClick={() => navigate(listUrl)}>
                        {t("selectedrequestbreeding.buttons.back")}
                    </button>
                    <button className="btn btn-primary btn-negative" onClick={() => {
                        rejectBreeding();
                    }}>
                        {t("selectedrequestbreeding.buttons.reject")}
                    </button>
                    <button
                        className="btn btn-primary btn-positive"
                        disabled={selectedReg == undefined || selectedReg.DOGMATCH == undefined || selectedReg.DOGMATCH.REGNO == undefined}
                        onClick={() => {
                            if (selectedReg != undefined && selectedReg.DOGMATCH != undefined && selectedReg.DOGMATCH.REGNO != undefined) {
                                const emailGuess = selectedReg?.DATA?.find((d: any) =>
                                    typeof d.answer === 'string' && /\S+@\S+\.\S+/.test(d.answer)
                                )?.answer || '';
                                setEditLinkEmail(emailGuess);
                                setPublishToList(false);
                                setSendEditLink(false);
                                setShowAcceptModal(true);
                            }
                        }}
                    >
                        {t("selectedrequestbreeding.buttons.accept")}
                    </button>
                    <button
                        className={"btn btn-primary btn-negative " + styles.deletebutton}
                        onClick={() => {
                            window.confirm(t("selectedrequestbreeding.deleteconfirm")) &&
                                deleteBreeding();
                        }}
                    >
                        {t("selectedrequestbreeding.buttons.delete")}
                    </button>
                </div>
    
                <hr/>
                <Container className={styles.container}>
                    <Row className={styles.row}>
                    {/* Kombinasjonsvurdering, only for breeds with breeding criteria */}
                    {femaleId != undefined && maleId != undefined && (
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="12"
                        xl="12"
                        xxl="12"
                        className={styles.col}
                    >
                        <CombinationAssessment damId={femaleId} sireId={maleId} />
                    </Col>
                    )}
                    {(femalePedigree.data || malePedigree.data) && (
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="12"
                        xl="12"
                        xxl="12"
                        className={styles.col}
                    >
                        <DiseaseAlert dam={femalePedigree.data} sire={malePedigree.data} hiddenDiseases={hiddenDiseases} showHalfSiblings={showHalfSiblings} />
                    </Col>
                    )}
                    {(femaleId != undefined || maleId != undefined) && (
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="12"
                        xl="12"
                        xxl="12"
                        className={styles.col}
                    >
                        <Card
                            title={t("selectedopenbreeding.familytree.title")}
                            children={<div>
                                <PedigreeCard
                                    dam={femalePedigree.data}
                                    sire={malePedigree.data}
                                    damId={femaleId}
                                    sireId={maleId}
                                    loading={femalePedigree.loading || malePedigree.loading}
                                    generations={familyTreeGenerations}
                                    onGenerationsChange={setFamilyTreeGenerations}
                                    hiddenDiseases={hiddenDiseases}
                                    onHiddenDiseasesChange={setHiddenDiseases}
                                    showHalfSiblings={showHalfSiblings}
                                    onShowHalfSiblingsChange={setShowHalfSiblings}
                                />
                            </div>}
                        ></Card>
                    </Col>
                    )}
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="6"
                        xl="6"
                        xxl="6"
                        className={styles.col}
                    >
                        <DogSummaryCard role="female" match={selectedReg?.DOGMATCH} loading={selectedReg == undefined} testStarts={femaleTestStarts}>
                            {/* Remove button / search only once the request is loaded */}
                            {selectedReg != undefined && (<>
                            {selectedReg?.REGNUMBER != undefined && (
                                <div style={{ marginTop: '15px' }}>
                                    <button 
                                        className="btn btn-sm btn-danger"
                                        onClick={() => {
                                            if (window.confirm(t("selectedrequestbreeding.removeconfirm"))) {
                                                removeDogMatch();
                                            }
                                        }}
                                    >
                                        {t("selectedrequestbreeding.buttons.removematch")}
                                    </button>
                                </div>
                            )}
                            {selectedReg?.REGNUMBER == undefined ?
                                <div>
                                    <p>{t("selectedrequestbreeding.datamatch.nomatch")}</p>
                                    <hr/>
                                    <div>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            placeholder={t("selectedrequestbreeding.search.placeholder")}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    searchDog();
                                                }
                                            }}
                                        />
                                        <button 
                                            className="btn btn-primary" 
                                            style={{ marginTop: '10px' }}
                                            onClick={searchDog}
                                            disabled={isSearching || !searchTerm}
                                        >
                                            {isSearching ? t("selectedrequestbreeding.search.searching") : t("selectedrequestbreeding.search.button")}
                                        </button>
                                    </div>
                                    {searchResults.length > 0 && (
                                        <div style={{ marginTop: '15px' }}>
                                            {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: 0, marginBottom: '8px' }}>{t("shared.search.maxresults")}</p>}
                                            <table className={styles.dogsearchtable} style={{ marginTop: '10px' }}>
                                                <thead>
                                                    <tr>
                                                        <th>{t("selectedrequestbreeding.results.fields.regnr")}</th>
                                                        <th>{t("selectedrequestbreeding.results.fields.dogname")}</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {searchResults.map((dog: any, index: number) => (
                                                        <tr key={index}>
                                                            <td>{dog.REGNO}</td>
                                                            <td>{dog.NAME}</td>
                                                            <td>
                                                                <button 
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => selectDog(dog)}
                                                                >
                                                                    {t("selectedrequestbreeding.search.select")}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {searchResults.length >= 50 && <p style={{ color: 'orange', marginTop: '8px', marginBottom: 0 }}>{t("shared.search.maxresults")}</p>}
                                        </div>
                                    )}
                                </div>
                            : null}
                            </>)}
                        </DogSummaryCard>
                    </Col>
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="6"
                        xl="6"
                        xxl="6"
                        className={styles.col}
                    >
                        <DogSummaryCard role="male" match={selectedReg?.MALEDOGMATCH} loading={selectedReg == undefined} testStarts={maleTestStarts}>
                            {/* Remove button / search only once the request is loaded */}
                            {selectedReg != undefined && (<>
                            {selectedReg?.MALE_REGNUMBER != undefined && (
                                <div style={{ marginTop: '15px' }}>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => {
                                            if (window.confirm(t("selectedrequestbreeding.removeconfirm"))) {
                                                removeMaleDogMatch();
                                            }
                                        }}
                                    >
                                        {t("selectedrequestbreeding.buttons.removematch")}
                                    </button>
                                </div>
                            )}
                            {selectedReg?.MALE_REGNUMBER == undefined ?
                                <div>
                                    <p>{t("selectedrequestbreeding.maledatamatch.nomatch")}</p>
                                    <hr/>
                                    <div>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={t("selectedrequestbreeding.search.maleplaceholder")}
                                            value={maleDogSearchTerm}
                                            onChange={(e) => setMaleDogSearchTerm(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    searchMaleDog();
                                                }
                                            }}
                                        />
                                        <button
                                            className="btn btn-primary"
                                            style={{ marginTop: '10px' }}
                                            onClick={searchMaleDog}
                                            disabled={isMaleDogSearching || !maleDogSearchTerm}
                                        >
                                            {isMaleDogSearching ? t("selectedrequestbreeding.search.searching") : t("selectedrequestbreeding.search.button")}
                                        </button>
                                    </div>
                                    {maleDogSearchResults.length > 0 && (
                                        <div style={{ marginTop: '15px' }}>
                                            {maleDogSearchResults.length >= 50 && <p style={{ color: 'orange', marginTop: 0, marginBottom: '8px' }}>{t("shared.search.maxresults")}</p>}
                                            <table className={styles.dogsearchtable} style={{ marginTop: '10px' }}>
                                                <thead>
                                                    <tr>
                                                        <th>{t("selectedrequestbreeding.results.fields.regnr")}</th>
                                                        <th>{t("selectedrequestbreeding.results.fields.dogname")}</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {maleDogSearchResults.map((dog: any, index: number) => (
                                                        <tr key={index}>
                                                            <td>{dog.REGNO}</td>
                                                            <td>{dog.NAME}</td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => selectMaleDog(dog)}
                                                                >
                                                                    {t("selectedrequestbreeding.search.select")}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {maleDogSearchResults.length >= 50 && <p style={{ color: 'orange', marginTop: '8px', marginBottom: 0 }}>{t("shared.search.maxresults")}</p>}
                                        </div>
                                    )}
                                </div>
                            : null}
                            </>)}
                        </DogSummaryCard>
                    </Col>
                    {selectedReg?.REGNUMBER != undefined && selectedReg?.MALE_REGNUMBER != undefined && (
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="12"
                        xl="12"
                        xxl="12"
                        className={styles.col}
                    >
                        <CollapsibleSection title={t("selectedrequestbreeding.matchbox.title")} storageKey="selectedrequestbreeding.simulation">
                            {isSimulating ? (
                                // Placeholder fields, as the simulation's label / value grid
                                <div className={styles.loadinggrid} aria-busy="true" aria-label={t("selectedrequestbreeding.simulation.loading")}>
                                    {[0, 1, 2, 3, 4, 5].map(i => (
                                        <div key={i} className={styles.loadingfield}>
                                            <Skeleton width={60 + (i % 3) * 20} height={10} />
                                            <Skeleton width={40} height={14} />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                (() => {
                                    const entries: [string, any][] = simulationResult?.offspring != null
                                        ? (Object.entries(simulationResult.offspring) as [string, any][])
                                            .filter(([key, val]) =>
                                                (typeof val === 'string' && val.startsWith('SPACER|')) ||
                                                (val != null && !key.endsWith('_HASNULL') && !(simulationResult.offspring[key + '_HASNULL'] > 0) &&
                                                    !((['GLAUKOM', 'ATAKSI', 'DWARF'].includes(key.toUpperCase())) && val === 'ikke testet/ikke testet'))
                                            )
                                            .filter(([, val], index, arr) => {
                                                if (!(typeof val === 'string' && val.startsWith('SPACER|'))) return true;
                                                for (let i = index + 1; i < arr.length; i++) {
                                                    const nextVal = arr[i][1];
                                                    if (typeof nextVal === 'string' && nextVal.startsWith('SPACER|')) break;
                                                    return true;
                                                }
                                                return false;
                                            })
                                        : [];
                                    const groups: { title: string; fields: [string, any][] }[] = [];
                                    let current: { title: string; fields: [string, any][] } | null = null;
                                    entries.forEach(([key, val]) => {
                                        if (typeof val === 'string' && val.startsWith('SPACER|')) {
                                            if (current) groups.push(current);
                                            current = { title: val.slice(7), fields: [] };
                                        } else {
                                            if (!current) current = { title: '', fields: [] };
                                            current.fields.push([key, val]);
                                        }
                                    });
                                    if (current) groups.push(current);
                                    return groups.map((group, gi) => {
                                        const ebvFields = group.fields.filter(
                                            ([key, val]) => key.toUpperCase().includes('EBV') && val !== null && val !== '' && !isNaN(Number(val))
                                        );
                                        const showChart = ebvFields.length >= 3;
                                        const radarData = {
                                            labels: ebvFields.map(([key]) => t("breedsimulator.results.traits." + key)),
                                            datasets: [
                                                {
                                                    label: 'Baseline',
                                                    data: ebvFields.map(() => 200),
                                                    backgroundColor: 'rgba(0,0,0,0)',
                                                    borderColor: 'rgba(180, 180, 180, 0.82)',
                                                    borderWidth: 2,
                                                    borderDash: [4, 3],
                                                    pointRadius: 0,
                                                },
                                                {
                                                    label: group.title,
                                                    data: ebvFields.map(([, val]) => Number(val)),
                                                    backgroundColor: '#1132191e',
                                                    borderColor: '#113219',
                                                    borderWidth: 1.5,
                                                    pointBackgroundColor: '#E3EAE5',
                                                }
                                            ]
                                        };
                                        const radarOptions = {
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                r: {
                                                    min: 150,
                                                    max: 250,
                                                    ticks: { display: false, stepSize: 25 },
                                                    pointLabels: { display: false },
                                                }
                                            }
                                        };
                                        return (
                                            <div key={gi} className={styles.simgroup}>
                                                {group.title && <div className={styles.fieldcategory}>{group.title}</div>}
                                                <div className={styles.simgroupcontent}>
                                                    <div className={styles.fieldlistgrid}>
                                                        {group.fields.map(([key, val], fi) => (
                                                            <div key={fi} className={styles.simitem}>
                                                                <span className={styles.simlabel}>{t("breedsimulator.results.traits." + key)}</span>
                                                                <div className={styles.fieldvaluerow}>
                                                                    <span className={styles.simvalue}>{String(val)}</span>
                                                                    <button
                                                                        className={styles.copybutton + (copiedKey === `sim-${key}` ? " " + styles.copied : "")}
                                                                        onClick={() => copyToClipboard(String(val), `sim-${key}`)}
                                                                        title="Copy"
                                                                    >{copiedKey === `sim-${key}` ? "✓" : "⎘"}</button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {showChart && (
                                                        <div className={styles.simchart}>
                                                            <Radar data={radarData} options={radarOptions} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()
                            )}
                        </CollapsibleSection>
                    </Col>
                    )}
                    <Col
                        xs="12"
                        sm="12"
                        md="12"
                        lg="12"
                        xl="12"
                        xxl="12"
                        className={styles.col}
                    >
                        <CollapsibleSection
                            title={t("selectedrequestbreeding.results.title")}
                            icon={
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                                    <path d="M14 3v5h5M9 13h6M9 17h6" />
                                </svg>
                            }
                            storageKey="selectedrequestbreeding.form"
                        >
                            {selectedReg != undefined ?
                                <div className={styles.fieldlist}>
                                    {selectedReg?.DATA?.map((field: any, index: number) => (
                                        <div key={index} className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>{field.TEXT}</span>
                                            <div className={styles.fieldvaluerow}>
                                                <span className={styles.fieldvalue}>{field.answer}</span>
                                                <button
                                                    className={styles.copybutton + (copiedKey === `data-${index}` ? " " + styles.copied : "")}
                                                    onClick={() => copyToClipboard(field.answer, `data-${index}`)}
                                                    title="Copy"
                                                >{copiedKey === `data-${index}` ? "✓" : "⎘"}</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            :
                                <div className={styles.loadinglist} aria-busy="true" aria-label={t("selectedrequestbreeding.results.loading")}>
                                    {[0, 1, 2, 3, 4].map(i => (
                                        <div key={i} className={styles.loadingfield}>
                                            <Skeleton width={90 + (i % 3) * 30} height={10} />
                                            <Skeleton width={120 + (i % 2) * 40} height={14} />
                                        </div>
                                    ))}
                                </div>
                            }
                        </CollapsibleSection>
                    </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};

class SelectedRequestBreedingProps {
    
}

export default SelectedRequestBreeding;